import React, { useEffect, useState } from "react";
import axios from "axios";
import { Doughnut, Line,Bar } from "react-chartjs-2";
import {
  Chart as ChartJS,
  ArcElement,
  LineElement,
  PointElement,
  BarElement,
  CategoryScale,
  LinearScale,
  Filler,
  Tooltip,
} from "chart.js";
import "bootstrap/dist/css/bootstrap.min.css";
import "./AdminAnalysis.css";
import { useAuth } from "../../Context/AuthContext";
import defaultImage from "./default-image.jpg";
import {
  FaCheckCircle,
  FaClock,
  FaExclamationCircle,
  FaChartLine,
  FaStar,
  FaRegStar,
} from "react-icons/fa";

import { FaStarHalfStroke } from "react-icons/fa6";
import { useNavigate } from "react-router-dom";

ChartJS.register(ArcElement, LineElement, PointElement, BarElement, CategoryScale, LinearScale, Filler, Tooltip);

const floatingLabelsPlugin = {
  id: "floatingLabels",
  afterDatasetsDraw(chart) {
    const { ctx } = chart;
    const meta = chart.getDatasetMeta(0);
    const values = chart.data.datasets[0]?.data || [];
    if (!meta?.data?.length || !values.length) return;

    const maxValue = Math.max(...values);
    const maxIndex = values.indexOf(maxValue);
    const lastIndex = values.length - 1;
    const midIndex = Math.floor(values.length / 2);

    const labelIndices = Array.from(new Set([midIndex, maxIndex, lastIndex])).sort((a, b) => a - b);

    const drawPill = (idx) => {
      const point = meta.data[idx];
      if (!point) return;

      const isPeak = idx === maxIndex;
      const isLatest = idx === lastIndex && !isPeak;
      const label = `${values[idx]}`;

      ctx.save();
      ctx.font = "600 12px 'Inter', sans-serif";
      const textWidth = ctx.measureText(label).width;
      const paddingX = 10;
      const pillW = textWidth + paddingX * 2;
      const pillH = 26;
      const radius = 13;
      const x = point.x - pillW / 2;
      const y = point.y - pillH - 16;

      const fill = isPeak ? "#f97316" : isLatest ? "#1e2433" : "#6366f1";

      ctx.beginPath();
      if (ctx.roundRect) {
        ctx.roundRect(x, y, pillW, pillH, radius);
      } else {
        ctx.moveTo(x + radius, y);
        ctx.arcTo(x + pillW, y, x + pillW, y + pillH, radius);
        ctx.arcTo(x + pillW, y + pillH, x, y + pillH, radius);
        ctx.arcTo(x, y + pillH, x, y, radius);
        ctx.arcTo(x, y, x + pillW, y, radius);
      }
      ctx.closePath();
      ctx.fillStyle = fill;
      ctx.shadowColor = "rgba(30, 36, 51, 0.18)";
      ctx.shadowBlur = 8;
      ctx.shadowOffsetY = 3;
      ctx.fill();

      ctx.shadowColor = "transparent";
      ctx.beginPath();
      ctx.moveTo(point.x - 5, y + pillH);
      ctx.lineTo(point.x + 5, y + pillH);
      ctx.lineTo(point.x, y + pillH + 6);
      ctx.closePath();
      ctx.fillStyle = fill;
      ctx.fill();

      ctx.fillStyle = "#ffffff";
      ctx.textAlign = "center";
      ctx.textBaseline = "middle";
      ctx.fillText(label, point.x, y + pillH / 2);
      ctx.restore();
    };

    labelIndices.forEach(drawPill);

    const peakPoint = meta.data[maxIndex];
    if (peakPoint) {
      ctx.save();
      ctx.beginPath();
      ctx.arc(peakPoint.x, peakPoint.y, 6, 0, Math.PI * 2);
      ctx.fillStyle = "#f97316";
      ctx.fill();
      ctx.lineWidth = 3;
      ctx.strokeStyle = "#ffffff";
      ctx.stroke();
      ctx.restore();
    }
  },
};

const PLACEHOLDER_WEEKLY_TREND = [
  { week: "Week 1", count: 8 },
  { week: "Week 2", count: 14 },
  { week: "Week 3", count: 10 },
  { week: "Week 4", count: 19 },
  { week: "Week 5", count: 15 },
  { week: "Week 6", count: 24 },
  { week: "Week 7", count: 20 },
  { week: "Week 8", count: 27 },
];

const AdminAnalysis = () => {
  const baseUrl = process.env.REACT_APP_COMPLAINTS_APP_BE_URL;

  const [complaintsData, setComplaintsData] = useState({
    resolved: 0,
    pending: 0,
    ongoing: 0,
  });

  const { adminCategory, user } = useAuth();
  const [weeklyTrend, setWeeklyTrend] = useState(PLACEHOLDER_WEEKLY_TREND);
const [loading, setLoading] = useState(true);

const [slaData, setSlaData] = useState({
  onTarget: 0,
  delayed: 0,
  onTargetComplaints: [],
  delayedComplaints: [],
});
// Add state to track which card/bar is active (null, 'onTarget', or 'delayed')
const [selectedBar, setSelectedBar] = useState(null);

// 👇 Add this
const [myRating, setMyRating] = useState({
  avgRating: 2.5,
  ratingCount: 0,
});



const navigate = useNavigate();
  useEffect(() => {
    const fetchData = async () => {
      if (!adminCategory) return;

      try {
        const token = localStorage.getItem("authToken");
        setLoading(true);

        const countResponse = await axios.get(
          `${baseUrl}/admin-api/complaints-count/${adminCategory}`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        setComplaintsData(countResponse.data);
        const ratingResponse = await axios.get(
  `${baseUrl}/admin-api/my-rating`,
  {
    headers: {
      Authorization: `Bearer ${token}`,
    },
  }
);

setMyRating(ratingResponse.data);
try {
  const categoriesParam = Array.isArray(adminCategory)
    ? adminCategory.join(",")
    : adminCategory;

  const slaResponse = await axios.get(
    `${baseUrl}/admin-api/sla-compliance/${encodeURIComponent(categoriesParam)}`,
    {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    }
  );
 

  setSlaData({
    onTarget: slaResponse.data.onTarget || 0,
    delayed: slaResponse.data.delayed || 0,
    onTargetComplaints: slaResponse.data.onTargetComplaints || [],
  delayedComplaints: slaResponse.data.delayedComplaints || [],
  });
} catch (slaError) {
  console.warn(
    "Failed to load SLA compliance metrics:",
    slaError
  );
}

        try {
          const trendResponse = await axios.get(
            `${baseUrl}/admin-api/weekly-trend/${adminCategory}`,
            { headers: { Authorization: `Bearer ${token}` } }
          );
          if (Array.isArray(trendResponse.data) && trendResponse.data.length) {
            setWeeklyTrend(trendResponse.data);
          }
        } catch (trendError) {
          console.warn("Weekly trend endpoint unavailable, using placeholder data.");
        }
      } catch (error) {
        console.error("Error fetching analysis data:", error);
        if (error.response && error.response.status === 401) {
          localStorage.removeItem("authToken");
          navigate("/complaints-website");
        }
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [adminCategory, baseUrl,navigate]);

  const { resolved = 0, pending = 0, ongoing = 0 } = complaintsData;
  const total = resolved + pending + ongoing;

  const segments = [
    { key: "resolved", label: "Resolved", value: resolved, color: "#10b981" },
    { key: "pending", label: "Pending", value: pending, color: "#f59e0b" },
    { key: "ongoing", label: "Ongoing", value: ongoing, color: "#ef4444" },
  ];

  const doughnutData = {
    labels: segments.map((s) => s.label),
    datasets: [
      {
        data: segments.map((s) => s.value),
        backgroundColor: segments.map((s) => s.color),
        borderColor: "#ffffff",
        borderWidth: 4,
        hoverOffset: 6,
      },
    ],
  };

  const doughnutOptions = {
    maintainAspectRatio: false,
    responsive: true,
    cutout: "72%",
    plugins: {
      legend: { display: false },
      tooltip: {
        backgroundColor: "#1e2433",
        titleColor: "#fff",
        bodyColor: "#e5e7eb",
        padding: 10,
        cornerRadius: 8,
      },
    },
  };

  const trendData = {
    labels: weeklyTrend.map((w) => w.week),
    datasets: [
      {
        data: weeklyTrend.map((w) => w.count),
        borderColor: "#6366f1",
        backgroundColor: "rgba(99, 102, 241, 0.12)",
        pointBackgroundColor: "#ffffff",
        pointBorderColor: "#6366f1",
        pointBorderWidth: 2,
        pointRadius: 4,
        pointHoverRadius: 6,
        borderWidth: 2.5,
        tension: 0.4,
        fill: true,
      },
    ],
  };

  const trendOptions = {
    maintainAspectRatio: false,
    responsive: true,
    layout: { padding: { top: 46 } },
    scales: {
      x: {
        grid: { display: false },
        ticks: { color: "#9ca3af", font: { size: 12 } },
      },
      y: {
        beginAtZero: true,
        grid: { color: "#f1f2f6" },
        ticks: {
          color: "#9ca3af",
          font: { size: 12 },
          stepSize: 1,
          precision: 0,
          callback: (value) => (Number.isInteger(value) ? value : null),
        },
        title: {
          display: true,
          text: "No. of Complaints",
          color: "#6b7280",
          font: { size: 12, weight: "600" },
        },
      },
    },
    plugins: {
      legend: { display: false },
      tooltip: {
        backgroundColor: "#1e2433",
        titleColor: "#fff",
        bodyColor: "#e5e7eb",
        padding: 10,
        cornerRadius: 8,
        displayColors: false,
      },
    },
  };
  const slaChartData = {
  labels: ["ON-TARGET (≤ 48h)", "DELAYED (> 48h)"],
  datasets: [
    {
      label: "Resolved Complaints",
      data: [slaData.onTarget, slaData.delayed],
      backgroundColor: ["#10b981", "#ef4444"],
      borderRadius: 6,
    },
  ],
};
//(Chart Click Handler)
const handleBarChartClick = (event, elements) => {
  if (elements && elements.length > 0) {
    const clickedIndex = elements[0].index;
    if (clickedIndex === 0) {
      setSelectedBar((prev) => (prev === "onTarget" ? null : "onTarget"));
    } else if (clickedIndex === 1) {
      setSelectedBar((prev) => (prev === "delayed" ? null : "delayed"));
    }
  }
};
//(Active List Selector)
const activeComplaintsList =
  selectedBar === "onTarget"
    ? slaData.onTargetComplaints || []
    : selectedBar === "delayed"
    ? slaData.delayedComplaints || []
    : [];

  if (loading) {
    return (
      <div className="statistics-page text-center">
        <div className="spinner-border text-light" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
        <p className="loading-text mt-3">Loading team analytics...</p>
      </div>
    );
  }
const renderStars = (rating) => {

  const full = Math.floor(rating);

  const half = rating % 1 >= 0.5;

  return (
    <>
      {[...Array(full)].map((_, i) => (
        <FaStar key={i} color="#F59E0B" />
      ))}

   {half && <FaStarHalfStroke color="#F59E0B" />}

      {[...Array(5 - full - (half ? 1 : 0))].map((_, i) => (
        <FaRegStar key={`e${i}`} color="#F59E0B" />
      ))}
    </>
  );
};
const getTimeAgo = (date) => {
  if (!date) return "";

  const now = new Date();
  const resolved = new Date(date);

  const diffMs = now - resolved;
  const diffMinutes = Math.floor(diffMs / (1000 * 60));
  const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
  const diffWeeks = Math.floor(diffDays / 7);

  if (diffMinutes < 1) return "just now";
  if (diffMinutes < 60) return `${diffMinutes} min ago`;
  if (diffHours < 24) return `${diffHours} hr ago`;
  if (diffDays < 7) return `${diffDays} days ago`;
  if (diffWeeks < 4) return `${diffWeeks} week${diffWeeks > 1 ? "s" : ""} ago`;

  return resolved.toLocaleDateString();
};
const formatResolutionTime = (hours) => {
  if (hours < 24) {
    return `${hours} hrs`;
  }

  const days = Math.floor(hours / 24);

  return `${days} day${days > 1 ? "s" : ""}`;
};
  return (
    <div className="statistics-page">
   <div className="page-header">

    <span className="page-eyebrow">
        OVERVIEW
    </span>

    <div className="header-title-row">

       <div className="title-section">
    <h2 className="page-title">
        Request Analytics
    </h2>

    <h5 className="page-admin-name">
        {user?.displayName || user?.email?.split("@")[0] || "Admin"}
    </h5>

    {Array.isArray(adminCategory) && adminCategory.length > 0 && (
        <>
            <p className="category-heading">Category:</p>
            <div className="admin-category-tags">
                {adminCategory.map((cat) => (
                    <span className="category-tag" key={cat}>{cat}</span>
                ))}
            </div>
        </>
    )}
</div>
        <div className="header-profile">
<img
    src={defaultImage}
    alt="Admin"
    className="header-profile-img"
/>
<div className="header-admin-name">
    {user?.displayName || "Admin"}
</div>

            <div className="header-rating">
    {renderStars(myRating.avgRating)}
</div>

            <small>
                {myRating.avgRating}/5
            </small>

        </div>

    </div>

</div>

   <div className="dashboard-top">

    <div className="summary-cards">

        <div className="card-box total">
            <div className="card-icon">
                <FaChartLine />
            </div>

            <h6 className="card-title1">
                Total Requests
            </h6>

            <div className="card-number">
                {total}
            </div>
        </div>

        <div className="card-box resolved">
            <div className="card-icon">
                <FaCheckCircle />
            </div>

            <h6 className="card-title1">
                Resolved
            </h6>

            <div className="card-number">
                {resolved}
            </div>
        </div>

        <div className="card-box pending">
            <div className="card-icon">
                <FaClock />
            </div>

            <h6 className="card-title1">
                Pending
            </h6>

            <div className="card-number">
                {pending}
            </div>
        </div>

        <div className="card-box ongoing">
            <div className="card-icon">
                <FaExclamationCircle />
            </div>

            <h6 className="card-title1">
                Ongoing
            </h6>

            <div className="card-number">
                {ongoing}
            </div>
        </div>

    </div>

  

</div>
{/* SLA Compliance Section */}
<div className="panel-card chart-card single-panel mb-4  position-relative">
  <h5 className="panel-title">Resolution Time Performance</h5>

  <p className="panel-subtitle">
     Breakdown of complaints resolved within and beyond the 48-hour target
  </p>

  <div className="sla-summary-cards row my-3">
    <div className="col-md-6 mb-2">
      <div
        className="card p-3 text-center border-0 shadow-sm"
        style={{ backgroundColor: "#f0fdf4" }}
      >
        <h6 className="text-success fw-bold">On-Target</h6>

        <h3 className="text-success my-1">
          {slaData.onTarget}
        </h3>

        <span className="text-muted small">
          Resolved within 48 hrs
        </span>
      </div>
    </div>

    <div className="col-md-6 mb-2">
      <div
        className="card p-3 text-center border-0 shadow-sm"
        style={{ backgroundColor: "#fef2f2" }}
      >
        <h6 className="text-danger fw-bold">Delayed</h6>

        <h3 className="text-danger my-1">
          {slaData.delayed}
        </h3>

        <span className="text-muted small">
          Resolved after 48 hrs
        </span>
      </div>
    </div>
  </div>

  <div className="chart-wrapper" style={{ height: "260px" }}>
    <Bar
      data={slaChartData}
      options={{
        responsive: true,
        maintainAspectRatio: false,
        onClick: handleBarChartClick,
      }}
    />
  </div>
  {/* DROPDOWN OVERLAY LIST */}
  {selectedBar && (
   <div
  className="card shadow-lg p-3 position-absolute bg-white border"
  style={{
     top: "120px",
      left: selectedBar === "onTarget" ? "27%" : "74%",
      transform: "translateX(-50%)",
      width: "400px",
      zIndex: 9999,
      borderRadius: "12px",
  }}
>
      <div className="d-flex justify-content-between align-items-center mb-2 pb-2 border-bottom">
        <span className={`fw-bold small ${selectedBar === "delayed" ? "text-danger" : "text-success"}`}>
          {selectedBar === "delayed" ? "🔴 Delayed Complaints" : "🟢 On-Target Complaints"}
        </span>
        <button
          onClick={() => setSelectedBar(null)}
          className="btn-close btn-sm"
          aria-label="Close"
        ></button>
      </div>

      <div style={{ maxHeight: "200px", overflowY: "auto" }}>
        {activeComplaintsList.length === 0 ? (
          <p className="text-muted small my-2 text-center">No complaints found</p>
        ) : (
          activeComplaintsList.map((item) => (
            <div
              key={item.complaint_id}
              className="p-2 mb-2 bg-light rounded d-flex justify-content-between align-items-center"
            >
              <div>
                <div className="fw-semibold text-dark small">{item.title}</div>
                <small className="text-muted">   Resolved in {formatResolutionTime(item.resolutionHours)}</small>
              </div>
              <button
                onClick={() => navigate(`/complaints-details/${item.complaint_id}`)}
                className="btn btn-sm btn-primary ms-2"
                style={{ fontSize: "11px", whiteSpace: "nowrap" }}
              >
                View Details
              </button>
            </div>
          ))
        )}
      </div>
    </div>
  )}
</div>


     {/*<div className="panel-card chart-card single-panel position-relative">
        <h5 className="panel-title">Resolution Breakdown</h5>
        <p className="panel-subtitle">How current requests are distributed by status</p>

        <div className="panel-body">
          {total === 0 ? (
            <div className="empty-state">No requests recorded yet.</div>
          ) : (
            <div className="doughnut-row">
              <div className="doughnut-wrap">
                <Doughnut data={doughnutData} options={doughnutOptions} />
                <div className="doughnut-center">
                  <span className="doughnut-total">{total}</span>
                  <span className="doughnut-total-label">Total</span>
                </div>
              </div>


              <ul className="legend-list">
                {segments.map((s) => (
                  <li className="legend-item" key={s.key}>
                    <span className="legend-dot" style={{ background: s.color }} />
                    <span className="legend-label">{s.label}</span>
                    <span className="legend-value">{s.value}</span>
                    <span className="legend-percent">
                      {total ? Math.round((s.value / total) * 100) : 0}%
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      </div>
      */}

      <div className="panel-card trend-card">
        <h5 className="panel-title">Resolved Requests Trend</h5>
        <p className="panel-subtitle">Resolved requests, grouped by week</p>
        <div className="trend-chart-container">
          <Line data={trendData} options={trendOptions} plugins={[floatingLabelsPlugin]} />
        </div>
      </div>
    </div>
  );
};

export default AdminAnalysis;